import {
  ActionButton,
  ActionsGroup,
  Container,
  FAB,
  Field,
  SearchFilter,
  SearchPagination,
  SortState,
  Table,
  usePagedQuery,
  useShowNotification
} from '@elotech/components';
import { TableContainer, alert } from 'common/components';
import { useLoading } from 'common/hooks';
import { CrudRoutesProps, useCrudRoutes } from 'hooks/useCrudRoutes';
import { useMemo } from 'react';
import { useHistory } from 'react-router';
import { CrudService } from 'service';

export type OutputCrudActionProps = {
  doSearch: () => Promise<any>;
  setLoading: (promise: Promise<any>) => Promise<any>;
};

type Props<T> = {
  children: any;
  service: CrudService<T>;
  searchFields: Field[];
  defaultSort?: SortState;
  title?: string;
  breadcrumb?: boolean;
  keyExtractor?: (entity: T) => string;
  renderExtraActions?: (
    entity: T,
    props: OutputCrudActionProps
  ) => React.ReactNode;
} & CrudRoutesProps;

export const CrudTable = <T extends object>({
  children,
  service,
  searchFields,
  defaultSort = { name: 'id', direction: 'asc' },
  visibility,
  paths,
  breadcrumb = true,
  keyExtractor = (entity: T) => `${(entity as any).id}`,
  renderExtraActions,
  title: inputTitle
}: Props<T>) => {
  const {
    viewPath,
    editPath,
    logPath,
    newPath,
    canDelete,
    title: crudTitle,
    pluralTitle
  } = useCrudRoutes({
    visibility,
    paths
  });
  const title = useMemo(() => inputTitle || crudTitle, [inputTitle, crudTitle]);

  const history = useHistory();
  const [isLoading, setLoading] = useLoading();
  const { values, loading, pagination, doSearch, doPagedSearch, doSortChange } =
    usePagedQuery({
      search: service.findAll,
      onError: error => Promise.reject(error)
    });

  const showNotification = useShowNotification();

  const onDelete = (id: any) => {
    alert({
      type: 'question',
      title: 'Remover',
      text: `Deseja mesmo excluir este ${title}?`,
      onConfirm: () =>
        setLoading(
          service
            .deleteById(id)
            .then(() =>
              showNotification({
                level: 'success',
                message: 'Registro excluído com sucesso!'
              })
            )
            .then(() => doSearch())
        )
    });
  };

  const outputProps: OutputCrudActionProps = useMemo(
    () => ({
      doSearch,
      setLoading
    }),
    [doSearch, setLoading]
  );

  return (
    <Container breadcrumb={breadcrumb}>
      <TableContainer>
        <SearchFilter fields={searchFields} search={doSearch} />
        <Table<T>
          values={values}
          keyExtractor={keyExtractor}
          loading={loading || isLoading}
          sortOnHeaderColumnClick={doSortChange}
          defaultSort={defaultSort}
        >
          {children}
          <Table.Column<T>
            header=""
            name="actions-column"
            value={entity => (
              <ActionsGroup>
                {renderExtraActions?.(entity, outputProps)}
                {!!viewPath && (
                  <ActionButton
                    label="Visualizar"
                    icon="eye"
                    path={viewPath.replace(':id', keyExtractor(entity))}
                  />
                )}
                {!!editPath && (
                  <ActionButton
                    label="Editar"
                    icon="pencil-alt"
                    path={editPath.replace(':id', keyExtractor(entity))}
                  />
                )}
                {canDelete && (
                  <ActionButton
                    label="Remover"
                    icon="trash"
                    onClick={() => onDelete(keyExtractor(entity))}
                  />
                )}
                {!!logPath && (
                  <ActionButton
                    label="Histórico"
                    icon="file-alt"
                    path={logPath.replace(':id', keyExtractor(entity))}
                  />
                )}
              </ActionsGroup>
            )}
          />
        </Table>
        {pagination && pagination.totalPages > 0 && (
          <SearchPagination page={pagination} searchWithPage={doPagedSearch} />
        )}
      </TableContainer>
      <div className="btn-save">
        {!!newPath && (
          <FAB
            icon="plus"
            title={`Novo ${title}`}
            onClick={() => history.push(newPath)}
          />
        )}
        {!!service.republish && (
          <FAB
            icon="fa fa-paper-plane"
            title={`Republicar ${pluralTitle}`}
            onClick={() => service.republish && setLoading(service.republish())}
          />
        )}
      </div>
    </Container>
  );
};
